//pages/news.js
import News from "../components/main-pages/News";
import Head from "next/head";
import { useRouter } from "next/router";
import HreflangLinks from "../components/common/HrefLinkTags";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import API from "../components/http/config.js";
import { withPageSuffix } from "../utils/seo";

const SITE = "https://www.prism-me.com";

const TITLE = "Digital Marketing News in Dubai & UAE | Prism Digital";
const TITLE_BASE = "Digital Marketing News in UAE";
const DESCRIPTION =
  "Explore the latest digital marketing news, industry insights, trends, and updates from Prism Digital, a leading digital marketing agency in Dubai, UAE.";

const parsePage = (value) => {
  const n = parseInt(value || "1", 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
};

export default function NewsPage({ initialNews = [], initialTotalPages = 1 }) {
  const router = useRouter();
  const page = parsePage(router.query.page);

  // Page 1 => clean URL, page 2+ => self-canonical
  const base = `${SITE}/news`;
  const canonical = page > 1 ? `${base}?page=${page}` : base;

  // Title + description (page number added on page 2+)
  let title = TITLE;
  let description = DESCRIPTION;

  if (page > 1) {
    const suffix = `Page ${page}`;
    title = `${TITLE_BASE} – ${suffix} | Prism Digital`;
    description = withPageSuffix(DESCRIPTION, suffix);
  }

  return (
    <>
      <HreflangLinks
        enPath="/news"
        params={{
          ...(page > 1 && { page }),
        }}
      />

      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
      </Head>
      <News initialNews={initialNews} initialTotalPages={initialTotalPages} />
    </>
  );
}

export async function getServerSideProps({ locale, query }) {
  // 301: no Arabic news yet. /ar/news (any ?page=) always goes to clean /news
  if (locale === "ar") {
    return {
      redirect: {
        destination: "/news",
        statusCode: 301,
      },
    };
  }

  const lang = "en";
  const page = parsePage(query.page);
  const limit = 12;

  // 301: the first page should exist at one address only (no ?page=1)
  if (query.page !== undefined && page === 1) {
    return {
      redirect: {
        destination: "/news",
        statusCode: 301,
      },
    };
  }

  try {
    const { data } = await API.get("/news/all/active", {
      params: { lang, page, limit },
    });

    const items = data?.items ?? [];

    // Out-of-range page: real 404, not an empty 200
    if (page > 1 && items.length === 0) {
      return { notFound: true };
    }

    return {
      props: {
        ...(await serverSideTranslations(lang, [
          "header",
          "footer",
          "news",
          "common",
        ])),
        initialNews: items,
        initialTotalPages: data?.meta?.totalPages ?? 1,
      },
    };
  } catch (e) {
    return {
      props: {
        ...(await serverSideTranslations(lang, [
          "header",
          "footer",
          "news",
          "common",
        ])),
        initialNews: [],
        initialTotalPages: 1,
      },
    };
  }
}
